import type { FC } from 'react'

import { Skeleton } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import orderBy from 'lodash/orderBy'

import { NoResults } from '@/components/NoResults'
import { useRenderLimit } from '@/hooks/use-render-limit'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'

import { useGroupedProductList, usePolicyId } from '../hooks'
import { usePolicyFilters } from '../Policy.context'

import ProductGroup from './ProductGroup'

import type { SortOrder } from '../../types'

import './products.scss'

export const Products: FC = () => {
  const policyId = usePolicyId()

  const filters = usePolicyFilters()
  const [sortOrder] = filters.sortOrder
  const [sortParam] = filters.sortParam

  const sortedInventoryList = getSortedProductList({ sortOrder, sortParam })

  const { isLoading, isRefetching } = useQuery(queries.policyProducts(policyId), () =>
    ecomPolicyResources.getPolicyProducts(policyId)
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedInventoryList, 30, 30)
  const tenElements = new Array(10)
  tenElements.fill(null)

  if (isLoading || isRefetching) {
    return (
      <div className="ecom-policy__products">
        {tenElements.map((_, index) => (
          <button
            className="ecom-policy__products__product"
            key={index}
            style={{ pointerEvents: 'none', padding: 0 }}
          >
            <Skeleton height="216px" width={250} />
          </button>
        ))}
      </div>
    )
  }

  if (sortedInventoryList.length === 0) return <NoResults />

  return (
    <div className="ecom-policy__products">
      {visibleList.map(group => (
        <ProductGroup
          key={group.groupedProducts.length === 1 ? group.groupedProducts[0].id : group.groupKey}
          {...group}
        />
      ))}

      <ListLoaderComponent />
    </div>
  )
}

function getSortedProductList({
  sortParam,
  sortOrder,
}: {
  sortOrder: SortOrder
  sortParam: 'priceFrom' | 'isSelected'
}) {
  const list = useGroupedProductList().groupedAndFiltered
  // When opening a policy, the default sorting should be selected products at the top, but the remaining products should be sorted by price, descending
  if (sortParam === 'isSelected') {
    return orderBy(list, ['isSelected', 'priceFrom'], ['desc', 'desc'])
  }
  return orderBy(list, sortParam, sortOrder)
}
